import {
  REGION_BY_COUNTRY,
  REGION_VALUES,
  type OpportunityRegion,
} from '@inference.engine/widgets/web';

export type MarketToken = string;

// eslint-disable-next-line i18next/no-literal-string -- wire token for the market filter encoding, not user-visible copy
export const GLOBAL_MARKET_TOKEN: MarketToken = 'Global';

export const STATIC_REGION_MARKET_TOKENS: readonly MarketToken[] = [
  GLOBAL_MARKET_TOKEN,
  ...REGION_VALUES,
];

// Market token = the bare region value (no `level:` prefix). The three
// namespaces are disjoint — "Global" is unique, REGION_VALUES is uppercase
// 4–5 chars, and country codes are ISO 3166-1 alpha-2 — so the backend
// classifies a token by lookup instead of parsing a delimiter.
export function serializeRegionToMarket(region: OpportunityRegion): MarketToken {
  return region.value;
}

const countryDisplayNames = (() => {
  try {
    // eslint-disable-next-line i18next/no-literal-string -- BCP 47 locale tag passed to Intl, not user-visible copy
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();

function countryLabel(code: string): string {
  return countryDisplayNames?.of(code) ?? code;
}

/** Human label for an OpportunityRegion struct on a card or detail view. */
export function displayRegion(region: OpportunityRegion | undefined): string | null {
  if (!region) return null;
  switch (region.level) {
    case 'country':
      return countryLabel(region.value);
    // 'global' and 'region' use the stored discriminator value verbatim (e.g. "Global", "APAC").
    default:
      return region.value;
  }
}

/** Human label for a serialized market token used in the filter dropdown. */
export function displayMarketToken(token: MarketToken): string {
  if (token === GLOBAL_MARKET_TOKEN) return token;
  if ((REGION_VALUES as readonly string[]).includes(token)) return token;
  const code = token.toUpperCase();
  if (code in REGION_BY_COUNTRY) return countryLabel(code);
  return token;
}

/** Parent region of a country code, or null if the code is unknown. */
export function parentRegion(code: string): string | null {
  return (REGION_BY_COUNTRY as Record<string, string>)[code] ?? null;
}
